<script lang="ts">
  import { onMount } from 'svelte';
  import { push } from 'svelte-spa-router';
  import Button from '../../Button.svelte';
  import SearchInput from '../../SearchInput.svelte';
  import Dropdown from '../../Dropdown.svelte';
  import Table from '../../Table.svelte';
  import Pagination from '../../Pagination.svelte';
  import Notification from '../../Notification.svelte';
  import AddCharityModal from './AddCharityModal.svelte';
  import DateRange from '../../DateRange.svelte';
  import { CharityService, type Charity } from '../../../lib/api/admin/charity.service';
  import { createPreferencesManager } from '../../../lib/utils/preferences';
  import LoadingSkeleton from '../../LoadingSkeleton.svelte';
  import EmptyState from '../../EmptyState.svelte';
  import ErrorState from '../../ErrorState.svelte';

  // Preferences manager
  const preferences = createPreferencesManager('charity-list');

  // When true, render the focused "Verification" view: only charities pending
  // registration (invited but not yet completed), with filters/metrics/add hidden.
  export let pendingOnly = false;

  let loading = false;
  let totalPages = 1;
  let tableData: Charity[] = [];
  let error: string | null = null;

  // Metrics
  let totalCharities = 0;
  let verifiedCharities = 0;
  let notVerifiedCharities = 0;
  let totalDonationsReceived = 0;

  // Filter states - initialize from preferences
  const savedFilters = preferences.getFilters();
  let searchQuery = savedFilters.searchQuery || '';
  let selectedVerificationStatus = savedFilters.selectedVerificationStatus || '';
  let selectedBankDetailsStatus = savedFilters.selectedBankDetailsStatus || '';
  let startDate = savedFilters.startDate || '';
  let endDate = savedFilters.endDate || '';
  let sortOption = savedFilters.sortOption || 'newest';
  
  // Pagination - initialize from preferences
  let currentPage = preferences.getCurrentPage() || 1;
  let itemsPerPage = preferences.getPageSize() || 10;

  // Notification state
  let notificationShow = false;
  let notificationType: 'success' | 'error' = 'success';
  let notificationMessage = '';

  let showAddModal = false;

  // Verification status options
  const verificationStatusOptions = [
    { uuid: '', name: 'All (registration)' },
    { uuid: '1', name: 'Registered — verified' },
    { uuid: '0', name: 'Registered — not verified' },
    { uuid: 'invited', name: 'Invited' },
    { uuid: 'rejected', name: 'Rejected' }
  ];

  const bankDetailsStatusOptions = [
    { uuid: '', name: 'All (bank details)' },
    { uuid: '1', name: 'Bank — verified' },
    { uuid: '0', name: 'Bank — unverified' }
  ];

  const sortOptions = [
    { uuid: 'newest', name: 'Newest first' },
    { uuid: 'oldest', name: 'Oldest first' },
    { uuid: 'name_asc', name: 'Name A–Z' },
    { uuid: 'name_desc', name: 'Name Z–A' }
  ];

  const columns = [
    {
      key: 'name',
      label: 'Charity Name',
      width: '35%',
      render: (value: string, row: Charity) => `
        <div class="charity-info">
          <div class="charity-logo" style="background-image: url(${row.logo || '/placeholder-logo.png'})"></div>
          <span>${value}</span>
        </div>
      `
    },
    {
      key: 'verified',
      label: 'Registration',
      width: '22%',
      align: 'center',
      render: (value: number, row: Charity) => {
        // Prefer the backend-computed registration_status (adds "Invited"), fall back to flags.
        const regStatus = row.registration_status
          || (row.rejected === true ? 'rejected' : (value === 1 ? 'verified' : 'not_verified'));
        const map: Record<string, { label: string; cls: string; icon: string }> = {
          rejected: { label: 'Rejected', cls: 'status-rejected', icon: 'rejected' },
          verified: { label: 'Verified', cls: 'status-registered', icon: 'registered' },
          invited: { label: 'Invited', cls: 'status-invited', icon: 'invited' },
          not_verified: { label: 'Not Verified', cls: 'status-pending', icon: 'pending' }
        };
        const m = map[regStatus] || map.not_verified;
        return `
          <div class="status-badge ${m.cls}">
            ${getStatusIcon(m.icon)}
            <span>${m.label}</span>
          </div>
        `;
      }
    },
    {
      key: 'bank_details_status',
      label: 'Bank details',
      width: '14%',
      align: 'center',
      render: (_v: string | undefined, row: Charity) => {
        const ok = row.bank_details_status === 'verified';
        const cls = ok ? 'bank-detail-badge verified' : 'bank-detail-badge unverified';
        const label = ok ? 'Verified' : 'Unverified';
        return `<span class="${cls}">${label}</span>`;
      }
    },
    {
      key: 'total_donation_received',
      label: 'Donation Received',
      width: '20%',
      render: (value: any, row: Charity) =>`$${row.total_donation_received.total_donation_received || 'N/A'}`
    },
    {
      key: 'created_at',
      label: 'Created on',
      width: '20%',
      render: (value: string | null | undefined) => (value ? CharityService.formatDate(value) : 'N/A')
    }
  ];

  function getStatusIcon(status: string): string {
    switch (status) {
      case 'registered':
        return '<svg class="status-icon" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"></path></svg>';
      case 'pending':
        return '<svg class="status-icon" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clip-rule="evenodd"></path></svg>';
      case 'rejected':
        return '<svg class="status-icon" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"></path></svg>';
      case 'invited':
        return '<svg class="status-icon" viewBox="0 0 20 20" fill="currentColor"><path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z"></path><path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"></path></svg>';
      default:
        return '';
    }
  }

  function showNotification(message: string, type: 'success' | 'error' = 'success') {
    notificationMessage = message;
    notificationType = type;
    notificationShow = true;
    setTimeout(() => notificationShow = false, 4000);
  }

  async function loadData() {
    loading = true;
    error = null;
    
    try {
      const filters: any = {
        page: currentPage,
        per_page: itemsPerPage,
        phrase: searchQuery || undefined
      };

      // Add verification status filter (or rejected / invited)
      if (selectedVerificationStatus === 'rejected') {
        filters.rejected = 1;
      } else if (selectedVerificationStatus === 'invited') {
        filters.invited = 1;
      } else if (selectedVerificationStatus !== '') {
        filters.verified = parseInt(selectedVerificationStatus);
      }

      if (selectedBankDetailsStatus !== '') {
        filters.bank_details_verified = selectedBankDetailsStatus === '1';
      }

      // Add date range filter for registration date (created_at)
      if (startDate) {
        filters.start_date = startDate;
      }
      if (endDate) {
        filters.end_date = endDate;
      }

      // Sort: map sortOption to sort_by and sort_direction
      switch (sortOption) {
        case 'oldest':
          filters.sort_by = 'created_at';
          filters.sort_direction = 'asc';
          break;
        case 'name_asc':
          filters.sort_by = 'name';
          filters.sort_direction = 'asc';
          break;
        case 'name_desc':
          filters.sort_by = 'name';
          filters.sort_direction = 'desc';
          break;
        default:
          filters.sort_by = 'created_at';
          filters.sort_direction = 'desc';
      }

      const response = await CharityService.getCharities(filters);
      
      if (response.success) {
        tableData = response.data;
        
        // Update pagination info from the response
        itemsPerPage = response.paging.per_page;
        totalPages = response.paging.last_page || 1;
        
        // Calculate metrics from the data
        totalCharities = response.paging.total || 0;
        verifiedCharities = tableData.filter(c => c.verified === 1).length;
        notVerifiedCharities = tableData.filter(c => c.verified === 0).length;
        totalDonationsReceived = tableData.reduce((sum, c) => {
          return sum + (c.total_donation_received?.total_donation_received || 0);
        }, 0);
      } else {
        error = 'Failed to load charities';
        showNotification(error, 'error');
      }
    } catch (err: any) {
      const errorMsg = err?.message || 'Failed to load charities';
      error = errorMsg;
      showNotification(errorMsg, 'error');
    } finally {
      loading = false;
    }
  }

  function handleSearch(event: CustomEvent<string>) {
    searchQuery = event.detail;
    currentPage = 1; // Reset to first page when searching
    preferences.update({ currentPage: 1, filters: { searchQuery, selectedVerificationStatus, selectedBankDetailsStatus, startDate, endDate, sortOption } });
    loadData();
  }

  function handleVerificationStatusChange(value: string) {
    selectedVerificationStatus = value;
    currentPage = 1;
    preferences.update({ currentPage: 1, filters: { searchQuery, selectedVerificationStatus, selectedBankDetailsStatus, startDate, endDate, sortOption } });
    loadData();
  }

  function handleBankDetailsStatusChange(value: string) {
    selectedBankDetailsStatus = value;
    currentPage = 1;
    preferences.update({ currentPage: 1, filters: { searchQuery, selectedVerificationStatus, selectedBankDetailsStatus, startDate, endDate, sortOption } });
    loadData();
  }

  function handleSortChange(value: string) {
    sortOption = value;
    currentPage = 1;
    preferences.update({ currentPage: 1, filters: { searchQuery, selectedVerificationStatus, selectedBankDetailsStatus, startDate, endDate, sortOption } });
    loadData();
  }

  function handleDateRangeChange(event: CustomEvent<{ startDate: string; endDate: string }>) {
    startDate = event.detail.startDate;
    endDate = event.detail.endDate;
    currentPage = 1;
    preferences.update({ currentPage: 1, filters: { searchQuery, selectedVerificationStatus, selectedBankDetailsStatus, startDate, endDate, sortOption } });
    loadData();
  }

  function handleResetFilters() {
    selectedVerificationStatus = '';
    selectedBankDetailsStatus = '';
    startDate = '';
    endDate = '';
    searchQuery = '';
    sortOption = 'newest';
    currentPage = 1;
    preferences.reset();
    loadData();
  }
  
  // Watch for filter changes and save preferences
  $: if (searchQuery !== undefined || selectedVerificationStatus !== undefined || selectedBankDetailsStatus !== undefined || startDate !== undefined || endDate !== undefined || sortOption !== undefined) {
    preferences.updateFilters({ searchQuery, selectedVerificationStatus, selectedBankDetailsStatus, startDate, endDate, sortOption });
  }
  
  // Watch for page size changes
  $: if (itemsPerPage !== undefined) {
    preferences.update({ pageSize: itemsPerPage });
  }
  
  // Watch for page changes
  $: if (currentPage !== undefined) {
    preferences.update({ currentPage });
  }


  function handlePageChange(event: CustomEvent<{ page: number }>) {
    const requestedPage = event.detail.page;
    
    // Update the page immediately for better UI responsiveness
    if (requestedPage > 0 && requestedPage <= totalPages) {
      currentPage = requestedPage;
      preferences.update({ currentPage });
      loadData();
    }
  }

  // Watch for changes to the currentPage and ensure it's within valid bounds
  $: {
    if (currentPage < 1) {
      currentPage = 1;
    } else if (totalPages > 0 && currentPage > totalPages) {
      currentPage = totalPages;
    }
  }

  function handleAddCharity() {
    showAddModal = true;
  }

  function handleCloseModal() {
    showAddModal = false;
  }

  async function handleSaveCharity(data: any) {
    // The charity data is already saved by the modal component
    // We just need to show a notification and reload the list
    showNotification('Charity added successfully', 'success');
    showAddModal = false;
    loadData(); // Reload the list
  }

  function handleRowClick(row: Charity) {
    const uuid = row.uuid;
    if (uuid) {
      push(`/charity-profile/${uuid}`);
    }
  }

  onMount(() => {
    if (pendingOnly) {
      // Verification view is locked to charities pending registration.
      selectedVerificationStatus = 'invited';
    } else {
      // Manage list always opens showing all charities (no remembered status filter).
      selectedVerificationStatus = '';
    }
    currentPage = 1;
    loadData();
  });

</script>

<div class="manage-list-page" class:pending-only={pendingOnly}>
  <div class="page-header">
    <p class="subtitle">
      {#if pendingOnly}
        Charities invited but not yet registered
      {:else}
        Manage and monitor all charities across the platform
      {/if}
    </p>
    {#if !pendingOnly}
    <div class="header-actions">
    <Button variant="primary" on:click={handleAddCharity} class="add-button">
      <div class="button-content">
        <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
          <path d="M12 4C11.4477 4 11 4.44772 11 5V11H5C4.44772 11 4 11.4477 4 12C4 12.5523 4.44772 13 5 13H11V19C11 19.5523 11.4477 20 12 20C12.5523 20 13 19.5523 13 19V13H19C19.5523 13 20 12.5523 20 12C20 11.4477 19.5523 11 19 11H13V5C13 4.44772 12.5523 4 12 4Z"/>
        </svg>
        Add Charity
      </div>
    </Button>
    </div>
    {/if}
  </div>

  {#if !pendingOnly}
  <!-- Metrics Grid -->
  <div class="metrics-grid">
    <div class="metric-card">
      <div class="metric-label">Total Charities</div>
      <div class="metric-value">{totalCharities.toLocaleString()}</div>
    </div>
    <div class="metric-card">
      <div class="metric-label">Verified</div>
      <div class="metric-value">{verifiedCharities.toLocaleString()}</div>
    </div>
    <div class="metric-card">
      <div class="metric-label">Not Verified</div>
      <div class="metric-value">{notVerifiedCharities.toLocaleString()}</div>
    </div>
    <div class="metric-card">
      <div class="metric-label">Total Donations</div>
      <div class="metric-value">${totalDonationsReceived.toLocaleString()}</div>
    </div>
  </div>
  {/if}

  {#if !pendingOnly}
  <div class="filters-section">
    <div class="filters-group">
      <div class="filter-item">
        <SearchInput
          value={searchQuery}
          on:search={handleSearch}
          placeholder="Search charities..."
          width="100%"
        />
      </div>
      <div class="filter-item">
        <Dropdown
          options={verificationStatusOptions}
          selectedValue={selectedVerificationStatus}
          placeholder="All (registration)"
          onSelectionChange={handleVerificationStatusChange}
        />
      </div>
      <div class="filter-item">
        <Dropdown
          options={bankDetailsStatusOptions}
          selectedValue={selectedBankDetailsStatus}
          placeholder="All (bank details)"
          onSelectionChange={handleBankDetailsStatusChange}
        />
      </div>
      <div class="filter-item">
        <Dropdown
          options={sortOptions}
          selectedValue={sortOption}
          placeholder="Sort by"
          onSelectionChange={handleSortChange}
        />
      </div>
      <div class="filter-item">
        <DateRange
          bind:startDate
          bind:endDate
          placeholder="Registration Date"
          on:change={handleDateRangeChange}
        />
      </div>
      <div class="filter-item">
        <Button variant="secondary" on:click={handleResetFilters} class="reset-button">
          Reset
        </Button>
      </div>
    </div>
  </div>
  {/if}

  <div class="table-section">
    {#if loading}
      <div class="loading-container">
        <div class="loading-spinner"></div>
        <p>Loading charities...</p>
      </div>
    {:else if error}
      <div class="error-container">
        <p class="error-message">{error}</p>
        <Button variant="secondary" on:click={loadData}>Try Again</Button>
      </div>
    {:else if tableData.length === 0}
      <EmptyState 
        message="No charities found"
        description={searchQuery || selectedVerificationStatus || selectedBankDetailsStatus || startDate || endDate ? "Try adjusting your filters to see more results." : "Get started by adding your first charity."}
        actionLabel={searchQuery || selectedVerificationStatus || selectedBankDetailsStatus || startDate || endDate ? "Reset Filters" : "Add Charity"}
        onAction={searchQuery || selectedVerificationStatus || selectedBankDetailsStatus || startDate || endDate ? handleResetFilters : handleAddCharity}
      />
    {:else}
      <div class="table-container">
      <Table 
        {columns}
        data={tableData}
        onRowClick={handleRowClick}
        {loading}
      />
      </div>
    {/if}
  </div>

  {#if totalPages > 1}
    <div class="pagination-container">
      <Pagination
        {currentPage}
        {totalPages}
        on:pageChange={handlePageChange}
      />
    </div>
  {/if}
</div>

<AddCharityModal 
  open={showAddModal}
  onClose={handleCloseModal}
  onSave={handleSaveCharity}
/>

<Notification 
  bind:show={notificationShow}
  type={notificationType}
  message={notificationMessage}
/>

<style>
  .page-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 1rem;
    margin-bottom: 1.5rem;
  }

  .subtitle {
    margin: 0;
    font-size: 0.875rem;
    color: #6b7280;
    line-height: 1.5;
    flex: 1;
  }

  .metrics-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 1rem;
    margin-bottom: 1rem;
  }

  .metric-card {
    background: white;
    border-radius: 12px;
    border: 1px solid #e5e7eb;
    padding: 1rem 1.25rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .metric-label {
    font-size: 0.875rem;
    font-weight: 500;
    color: #6b7280;
    margin: 0;
  }

  .metric-value {
    font-size: 1.5rem;
    font-weight: 600;
    color: #111827;
  }

  .header-actions {
    display: flex;
    gap: 1rem;
    align-items: center;
    flex-shrink: 0;
  }

  .filters-section {
    margin-bottom: 1rem;
    width: 100%;
  }

  .filters-group {
    display: flex;
    gap: 1rem;
    align-items: flex-start;
    flex-wrap: wrap;
    width: 100%;
  }

  .filter-item {
    flex: 1 1 auto;
    min-width: 140px;
    max-width: 300px;
  }

  .filter-item:has(.reset-button) {
    flex: 0 0 auto;
    max-width: none;
    align-self: center;
  }

  .filter-item :global(.search-input-wrapper),
  .filter-item :global(.dropdown-wrapper),
  .filter-item :global(.date-range-wrapper) {
    width: 100%;
  }

  .button-content {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  :global(.add-button) {
    background-color: #f59e0b !important;
    color: white !important;
    border-radius: 100px !important;
    padding: 10px 20px !important;
    height: 52px !important;
    font-size: 1rem !important;
    font-weight: 500 !important;
  }

  :global(.add-button:hover) {
    background-color: #d97706 !important;
  }

  :global(.reset-button) {
    background-color: white !important;
    color: #374151 !important;
    border: 1px solid #d1d5db !important;
    border-radius: 8px !important;
    padding: 0.75rem 1rem !important;
    font-size: 0.875rem !important;
    font-weight: 500 !important;
  }

  :global(.reset-button:hover) {
    background-color: #f9fafb !important;
    border-color: #9ca3af !important;
  }


  .table-section {
    background: white;
    border-radius: 12px;
    border: 1px solid #e5e7eb;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
    overflow: hidden;
    margin-bottom: 1rem;
  }

  .table-container {
    overflow-x: auto;
  }

  .loading-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 3rem;
    min-height: 200px;
  }

  .loading-spinner {
    width: 2rem;
    height: 2rem;
    border: 3px solid #f3f4f6;
    border-top: 3px solid #f0b429;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 1rem;
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }

  .error-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 3rem;
    min-height: 200px;
  }

  .error-message {
    color: #dc2626;
    margin-bottom: 1rem;
    font-size: 1rem;
  }

  :global(.status-badge) {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.25rem 0.75rem;
    border-radius: 9999px;
    font-size: 0.875rem;
  }

  :global(.status-registered) {
    background-color: #dcfce7;
    color: #166534;
  }

  :global(.status-pending) {
    background-color: #fef3c7;
    color: #92400e;
  }

  :global(.status-rejected) {
    background-color: #fee2e2;
    color: #991b1b;
  }

  :global(.status-rejected) {
    background-color: #fee2e2;
    color: #991b1b;
  }

  :global(.status-invited) {
    background-color: #eef2ff;
    color: #4338ca;
  }

  :global(.status-icon) {
    width: 16px;
    height: 16px;
  }

  :global(.bank-detail-badge) {
    display: inline-flex;
    align-items: center;
    padding: 0.25rem 0.75rem;
    border-radius: 9999px;
    font-size: 0.75rem;
    font-weight: 500;
  }

  :global(.bank-detail-badge.verified) {
    background-color: #d1fae5;
    color: #065f46;
  }

  :global(.bank-detail-badge.unverified) {
    background-color: #fef3c7;
    color: #92400e;
  }

  :global(.view-button-charity) {
    background-color: #111827;
    color: white;
    border: none;
    padding: 0.375rem 1rem;
    border-radius: 50px;
    font-size: 0.875rem;
    cursor: pointer;
    transition: background-color 0.2s;
  }

  :global(.view-button-charity:hover) {
    background-color: #1f2937;
  }

  .pagination-container {
    padding: 1rem 1.5rem;
    border-top: 1px solid #f3f4f6;
  }

  @media (max-width: 768px) {
    .page-header {
      flex-direction: column;
      align-items: stretch;
    }

    .header-actions {
      width: 100%;
      flex-direction: column;
    }

    .metrics-grid {
      grid-template-columns: repeat(2, 1fr);
    }

    .filters-group {
      flex-direction: column;
    }

    .filter-item {
      width: 100%;
      max-width: none;
    }
  }

  @media (max-width: 480px) {
    .metrics-grid {
      grid-template-columns: 1fr;
    }
  }

</style> 